import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Avatar from "components/@extended/Avatar";
import ProfileTab from "./ProfileTab";
import MainCard from "components/MainCard";
import Transitions from "components/@extended/Transitions";
import { V2 } from "layout/Dashboard/Header/headerV2Tokens";

function TabPanel({ children, value, index, ...other }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
      sx={{ p: 1 }}
    >
      {value === index && children}
    </Box>
  );
}

export default function ProfilePage({
  admin,
  open: externalOpen,
  setOpen: setExternalOpen,
  userData,
  anchorRef: externalAnchorRef,
  downLG,
  hideTrigger = false,
}) {
  const theme = useTheme();
  const internalAnchorRef = useRef(null);
  const buttonRef = useRef(null);
  const [internalOpen, setInternalOpen] = useState(false);

  const popperAnchorRef = externalAnchorRef || internalAnchorRef;
  const buttonAnchorRef = externalAnchorRef ? buttonRef : internalAnchorRef;
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = setExternalOpen || setInternalOpen;

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    const anchorElement = popperAnchorRef.current;
    const buttonElement = buttonRef.current;
    if (
      (anchorElement && anchorElement.contains(event.target)) ||
      (buttonElement && buttonElement.contains(event.target))
    ) {
      return;
    }
    setOpen(false);
  };

  function stringAvatar(firstName, lastName) {
    if (!firstName || !lastName)
      return {
        sx: {
          bgcolor: "primary.100",
          fontWeight: 700,
          fontSize: 12,
        },
      };

    return {
      sx: {
        bgcolor: "primary.100",
        fontWeight: 700,
        fontSize: 12,
      },
      children: `${firstName[0]}${lastName[0]}`.toUpperCase(),
    };
  }

  return (
    <Box
      sx={{
        flexShrink: 0,
        ...(hideTrigger
          ? { position: "absolute", width: 0, height: 0, overflow: "visible" }
          : { ml: 0.7 }),
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {!hideTrigger && (
        <ButtonBase
          sx={{
            p: 0.25,
            borderRadius: 0,
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.secondary.dark}`,
              outlineOffset: 2,
            },
          }}
          aria-label="open profile"
          ref={buttonAnchorRef}
          aria-controls={open ? "profile-grow" : undefined}
          aria-haspopup="true"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
        >
          {downLG && (
            <Avatar
              {...stringAvatar(userData?.first_name, userData?.last_name)}
              src={userData?.profile_image_url}
              sx={{ width: 35, height: 35, fontSize: 14 }}
            />
          )}
        </ButtonBase>
      )}
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={popperAnchorRef.current}
        role={undefined}
        transition
        disablePortal={false}
        sx={{ zIndex: theme.zIndex.modal + 1 }}
        popperOptions={{
          modifiers: [{ name: "offset", options: { offset: [0, 9] } }],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position="top-right"
            in={open}
            {...TransitionProps}
          >
            <Paper
              sx={{
                boxShadow: "0 12px 40px rgba(15, 23, 32, 0.12)",
                width: 288,
                minWidth: 260,
                maxWidth: 300,
                borderRadius: 0,
                border: `1px solid ${V2.line}`,
                overflow: "hidden",
                fontFamily: V2.fontBody,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  elevation={0}
                  border={false}
                  content={false}
                  sx={{ borderRadius: 0 }}
                >
                  <ProfileTab admin={admin} onClose={() => setOpen(false)} />
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number,
  index: PropTypes.number,
  other: PropTypes.any,
};

ProfilePage.propTypes = {
  admin: PropTypes.bool,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  userData: PropTypes.object,
  anchorRef: PropTypes.object,
  downLG: PropTypes.bool,
  hideTrigger: PropTypes.bool,
};
